/**
 * Step-line chart for CEFR progression (A1 → C2) with transition markers.
 */
import type { DailyStatsRow, CefrEvent } from "../../api";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

export default function CefrStepLine({
  rows,
  events,
  currentLevel,
}: {
  rows: DailyStatsRow[];
  events: CefrEvent[];
  currentLevel: string;
}) {
  const W = 640;
  const H = 220;
  const PAD = { t: 20, r: 20, b: 30, l: 44 };
  const innerW = W - PAD.l - PAD.r;
  const innerH = H - PAD.t - PAD.b;
  const n = rows.length;

  if (n === 0) {
    return (
      <div className="card">
        <div className="mb-3">
          <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
            CEFR progression
          </div>
          <div className="text-[11px] text-ink-faint mt-0.5">
            currently <b>{currentLevel}</b>
          </div>
        </div>
        <div className="text-ink-faint text-sm py-8 text-center">no data yet</div>
      </div>
    );
  }

  const levels = rows.map((r) => r.cefr_level || "A1");
  const top = 5; // index range 0..5
  const xStep = n > 1 ? innerW / (n - 1) : innerW;

  const pts = levels
    .map((lv, i) => {
      const idx = Math.max(0, LEVELS.indexOf(lv));
      const x = PAD.l + (n > 1 ? i * xStep : innerW / 2);
      const y = PAD.t + innerH * (1 - idx / top);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  // Match events to day indices by comparing dates.
  const eventMarkers = events
    .map((ev) => {
      const date = new Date(ev.epoch * 1000).toISOString().slice(0, 10);
      const i = rows.findIndex((r) => r.date === date);
      if (i < 0) return null;
      const toIdx = Math.max(0, LEVELS.indexOf(ev.to_level));
      const fromIdx = Math.max(0, LEVELS.indexOf(ev.from_level));
      const arrow = toIdx > fromIdx ? "↑" : "↓";
      const color = toIdx > fromIdx ? "#3f6a44" : "#b9523a";
      return {
        x: PAD.l + (n > 1 ? i * xStep : innerW / 2),
        y: PAD.t + innerH * (1 - toIdx / top),
        arrow,
        to: ev.to_level,
        color,
      };
    })
    .filter(Boolean) as { x: number; y: number; arrow: string; to: string; color: string }[];

  const transitionCount = events.length;

  return (
    <div className="card">
      <div className="mb-3">
        <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">
          CEFR progression
        </div>
        <div className="text-[11px] text-ink-faint mt-0.5">
          currently <b>{currentLevel}</b>
          {transitionCount > 0
            ? ` · ${transitionCount} transition${transitionCount !== 1 ? "s" : ""}`
            : ""}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <g stroke="#e4e1db" strokeWidth="1">
          {LEVELS.map((_, i) => (
            <line
              key={i}
              x1={PAD.l}
              y1={PAD.t + (innerH * i) / 5}
              x2={W - PAD.r}
              y2={PAD.t + (innerH * i) / 5}
            />
          ))}
        </g>
        <g fill="#6d6a63" fontSize="10" textAnchor="end">
          {LEVELS.map((lbl, i) => (
            <text key={lbl} x={PAD.l - 4} y={PAD.t + (innerH * (5 - i)) / 5 + 3}>
              {lbl}
            </text>
          ))}
        </g>
        <polyline
          fill="none"
          stroke="#a25618"
          strokeWidth="2.5"
          strokeLinejoin="round"
          points={pts}
        />
        {eventMarkers.map((m, i) => (
          <g key={i}>
            <circle cx={m.x} cy={m.y} r={5} fill={m.color} />
            <text
              x={m.x}
              y={m.y - 10}
              fill={m.color}
              fontSize="11"
              textAnchor="middle"
            >
              {m.arrow}
              {m.to}
            </text>
          </g>
        ))}
        <g fill="#6d6a63" fontSize="10" textAnchor="middle">
          <text x={PAD.l} y={H - 8}>
            d1
          </text>
          <text x={W - PAD.r} y={H - 8}>
            d{n}
          </text>
        </g>
      </svg>
    </div>
  );
}
